import { type GetApiEmployeesError, type GetApiEmployeesResponse, useGetApiEmployees } from '@sit/openapi';
import { skipToken, type UseQueryOptions } from '@tanstack/react-query';
import { useIsStaff, useIsCrew } from '@web/containers/Timesheets/hooks';
import { includeIntacctId, sortObjectsByNameAscUnsafe } from '@web/helpers/utils';
import { useCurrentEntity } from '@web/hooks/use-current-entity';
import { useMemo } from 'react';
import { useShowIntacctId } from '../show-intacct-id';

export function useGetEmployees(
  companyId?: string,
  options?: Omit<
    UseQueryOptions<GetApiEmployeesResponse, GetApiEmployeesError, GetApiEmployeesResponse>,
    'queryKey' | 'queryFn' | 'initialData'
  > & {
    staff?: boolean;
  },
) {
  const isStaff = useIsStaff();
  const isCrew = useIsCrew();
  const { staff = isStaff || isCrew, ...queryOptions } = options ?? {};
  const currentCompanyId = useCurrentEntity()?.id;
  const selectedCompanyId = companyId ?? currentCompanyId;
  const result = useGetApiEmployees(
    staff && selectedCompanyId
      ? {
          queryParams: {
            companyId: selectedCompanyId,
          },
        }
      : skipToken,
    {
      ...queryOptions,
      enabled: queryOptions.enabled !== false,
    },
  );
  const showIntacctId = useShowIntacctId();
  const emloyeesData = result.data?.employees;
  const employees = useMemo(() => {
    if (!emloyeesData) return undefined;
    return (showIntacctId ? emloyeesData.map((d) => includeIntacctId(d, { fieldName: 'employeeId' })) : [...emloyeesData]).sort(
      sortObjectsByNameAscUnsafe,
    );
  }, [emloyeesData, showIntacctId]);
  // `data` stays undefined until loaded so consumer `!data` guards work.
  const data = useMemo(() => (employees ? { employees } : undefined), [employees]);
  return { ...result, data };
}
